import { useEffect, useRef, useState, type MouseEvent as MouseEvent_ } from 'react'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import {
  editorSaveLabel,
  handleEditClick,
  handleEditGroupToggle,
  handleMoreSettingsClick,
  handleNewMsgClick,
  handleSaveClick,
} from '../menuActions'
import type { PortalMenuActionLabels } from '../types'

// The shared menu's morphing action control ("super button"):
//   mode 'main' → the "+" edit group (Edit / New msg)
//   mode 'edit' → Save/Update + config (the active editor button)
export function MenuActionButton({ mode, labels, canNewMsg = true }: { mode: 'main' | 'edit'; labels: PortalMenuActionLabels; canNewMsg?: boolean }) {
  if (mode === 'edit') {
    return (
      <li className="md-sidebar__new-sub-item md-sidebar__new-sub-item--active flex items-center px-1">
        <div className="md-sidebar__new-sub-button md-sidebar__new-sub-button--active flex h-[34px] items-stretch overflow-hidden rounded-[9px] bg-[#f59e0b] text-[#1f2937] shadow-[0_4px_14px_rgba(245,158,11,0.4)]">
          <div
            className="md-sidebar__new-sub-label md-sidebar__new-sub-label--active flex h-full min-w-[60px] flex-[0_0_auto] cursor-pointer items-center whitespace-nowrap px-3.5 text-[13px] font-bold text-[#1f2937]"
            onClick={handleSaveClick}
          >
            {editorSaveLabel(labels)}
          </div>
          <div
            {...legacyContractDataAttrs('sidebar.moreSettings')}
            className="md-sidebar__new-sub-icon md-sidebar__new-sub-icon--active flex h-full w-7 flex-[0_0_auto] cursor-pointer items-center justify-center bg-black/10 transition-colors duration-150 hover:bg-black/20"
            onClick={handleMoreSettingsClick}
          >
            <div className="md-sidebar__new-sub-options md-sidebar__new-sub-options--active flex w-7 flex-[0_0_auto] items-center justify-center text-[11px] text-[#1f2937]">▾</div>
          </div>
        </div>
      </li>
    )
  }

  return <EditGroupButton canNewMsg={canNewMsg} labels={labels} />
}

// The "+" dropdown is REACT-OWNED. It used to rely purely on the legacy
// edit-buttons toggle, which only flips the legacy class
// `mcss-menu_pre_edit-cssm` on the menu container — no rule in this app reads
// that class, so the click did nothing and the group opened on hover alone.
// The legacy toggle is still dispatched through menuActions (it is the legacy
// menu's own state), but the open/close state lives here.
function EditGroupButton({ canNewMsg, labels }: { canNewMsg: boolean; labels: PortalMenuActionLabels }) {
  const [isOpen, setIsOpen] = useState(false)
  const itemRef = useRef<HTMLLIElement | null>(null)

  useEffect(() => {
    if (!isOpen) return
    function onPointerDown(event: MouseEvent) {
      if (!itemRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  function handleToggle(event: MouseEvent_<HTMLElement>) {
    setIsOpen(open => !open)
    handleEditGroupToggle(event)
  }

  function closeAfter(run: () => void) {
    return () => {
      setIsOpen(false)
      run()
    }
  }

  return (
    <li ref={itemRef} className="md-sidebar__edit-item group relative -my-1.5 flex h-[46px] w-9 items-center justify-center bg-transparent py-1.5">
      <span
        {...legacyContractDataAttrs('sidebar.editGroup.button')}
        className="md-sidebar__edit-toggle m-0 flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-[9px] bg-[#2563eb] text-xl font-semibold leading-none text-white transition-[background,transform] duration-150 group-hover:-translate-y-px group-hover:bg-[#1d4ed8]"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        +
      </span>
      {/* Stays MOUNTED while closed: it carries a legacy contract, and legacy
          hides it outright on a locked message (m09 `hide(edit_group())`) —
          which must keep winning over the React open state, exactly as the
          chrome projector applies it. */}
      <div
        {...legacyContractDataAttrs('sidebar.editGroup.body')}
        className={`md-sidebar__edit-group absolute left-0 top-[46px] z-[32700] min-w-[168px] flex-col items-stretch gap-0.5 rounded-xl border border-[rgba(15,23,42,0.08)] bg-white p-1.5 shadow-[0_16px_40px_rgba(15,23,42,0.2)] ${isOpen ? 'flex' : 'hidden'}`}
      >
        <div className="md-sidebar__menu-button flex min-w-16 cursor-pointer items-center whitespace-nowrap rounded-lg bg-transparent px-3 py-[9px] font-medium text-[#0f172a] transition-colors duration-150 hover:bg-[#2563eb1a] hover:text-[#2563eb]" onClick={closeAfter(handleEditClick)}>
          {labels.edit}
        </div>
        {canNewMsg ? (
          <div className="md-sidebar__menu-button flex min-w-16 cursor-pointer items-center whitespace-nowrap rounded-lg bg-transparent px-3 py-[9px] font-medium text-[#0f172a] transition-colors duration-150 hover:bg-[#2563eb1a] hover:text-[#2563eb]" onClick={closeAfter(handleNewMsgClick)}>
            {labels.newMessage}
          </div>
        ) : null}
      </div>
    </li>
  )
}
